<template>
  <div class="bg-primary-orange">
    <div class="flex justify-between items-center text-gray-800 z-10 px-6 h-24">
      <button @click="backToRecipes" class="p-2 rounded-full active:bg-orange-400" type="button">
        <svg
          class="stroke-current h-6 w-6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button @click="moreActionsOpen = true" class="p-2 rounded-full active:bg-orange-400">
        <svg
          class="stroke-current h-6 w-6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <!-- <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
          <path d="M17 21v-8H7v8M7 3v5h8" />-->
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      </button>
    </div>
    <div class="flex-1 shadow-lg overflow-y-scroll rounded-t-xl bg-gray-100">
      <div v-if="recipe">
        <div class="bg-white shadow p-6 text-gray-800">
          <p class="text-3xl font-heading resize-none">{{ recipe.title }}</p>
          <p class="mt-2">{{ recipe.description }}</p>
          <div class="flex flex-wrap mt-4 -m-1">
            <div
              v-if="recipe.meal_type"
              class="inline-flex items-center bg-orange-200 text-orange-900 px-3 py-1 m-1 rounded-lg"
            >
              <svg
                class="stroke-current h-5 w-5 mr-2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
              </svg>
              <span>{{ recipe.meal_type }}</span>
            </div>
            <div
              v-if="recipe.prep_time"
              class="inline-flex items-center bg-purple-200 text-purple-900 px-3 py-1 m-1 rounded-lg"
            >
              <svg
                class="stroke-current h-4 w-4 mr-2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span>Prep: {{ recipe.prep_time.hr }}:{{ recipe.prep_time.min }}</span>
            </div>
            <div
              v-if="recipe.cook_time"
              class="inline-flex items-center bg-purple-200 text-purple-900 px-3 py-1 m-1 rounded-lg"
            >
              <svg
                class="stroke-current h-4 w-4 mr-2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span>Cook: {{ recipe.cook_time.hr }}:{{ recipe.cook_time.min }}</span>
            </div>
          </div>
          <div class="flex flex-wrap -m-1 mt-4">
            <template v-for="(tag, i) in recipe.tags">
              <div
                class="text-sm bg-gray-200 rounded-lg px-3 py-1 m-1 inline-flex items-center"
                :key="i"
              >
                <svg
                  class="stroke-current h-4 w-4 mr-2"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01"
                  />
                </svg>
                {{ tag }}
              </div>
            </template>
          </div>
        </div>
        <div class="mt-2 shadow bg-white px-6">
          <div class="-mx-3 flex">
            <button
              @click="currentTab = 'ingredients'"
              class="text-teal-700 text-sm py-4 mx-3"
              :class="{ 'border-b border-teal-700': currentTab == 'ingredients' }"
            >Ingredients</button>
            <button
              @click="currentTab = 'method'"
              class="text-teal-700 text-sm py-4 mx-3"
              :class="{ 'border-b border-teal-700': currentTab == 'method' }"
            >Method</button>
          </div>
        </div>
        <div v-if="currentTab == 'ingredients'" class="p-6">
          <h2 class="text-xl font-heading text-gray-800 mb-6">Ingredients</h2>
          <div class="-my-3">
            <template v-for="(ingredient, i) in recipe.ingredients">
              <div class="bg-white px-4 py-4 rounded-lg my-3 shadow" :key="i">
                <span v-if="ingredient.qty">{{ ingredient.qty }}&nbsp;</span>
                <span v-if="ingredient.unit">{{ ingredient.unit }}&nbsp;</span>
                <span>{{ ingredient.name }}</span>
              </div>
            </template>
            <div v-if="recipe.ingredients && recipe.ingredients.length == 0" class="flex justify-center py-5">
              <span>No Ingredients</span>
            </div>
          </div>
        </div>
        <div v-if="currentTab == 'method'" class="p-6">
          <h2 class="text-xl font-heading text-gray-800 mb-6">Method</h2>
          <div class="-my-3">
            <template v-for="(method, i) in recipe.method">
              <div
                class="bg-white px-4 py-4 rounded-lg my-3 shadow"
                :key="i"
              >{{ i + 1 }}: {{ method }}</div>
            </template>
            <div v-if="recipe.method && recipe.method.length == 0" class="flex justify-center py-5">
              <span>No Method</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <more-actions
      :isOpen="moreActionsOpen"
      @close="moreActionsOpen = false"
      @deleteRecipe="deleteRecipe"
      @editRecipe="editRecipe"
    />
  </div>
</template>

<script>
import MoreActions from "@/components/Recipe/MoreActions.vue";

export default {
  name: "recipe",
  components: {
    MoreActions
  },
  data() {
    return {
      recipe: {},
      currentTab: "ingredients",
      moreActionsOpen: false
    };
  },
  mounted() {
    this.$store.dispatch("setLoading", true);
    this.recipe = this.$_.find(
      this.$store.state.allRecipes,
      r => r.id == this.$router.currentRoute.params.recipeId
    );
    this.$store.dispatch("setLoading", false);
    if (this.recipe == null) {
      this.$router.replace("/");
    }
  },
  methods: {
    backToRecipes: function() {
      this.$router.replace("/");
    },
    deleteRecipe: async function() {
      this.moreActionsOpen = false;
      this.$store.dispatch("setLoading", true);
      try {
        await this.FireService.deleteRecipe(this.recipe.id);
        this.$router.replace("/");
      } catch (error) {
        console.error("Error updating document: ", error);
      }
      this.$store.dispatch("setLoading", false);
    },
    editRecipe: function() {
      this.moreActionsOpen = false;
      this.$router.push({ name: "edit", params: { recipeId: this.recipe.id } });
    }
  }
};
</script>